import React from 'react';
import { Plus, X, Info } from 'lucide-react';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Button } from '../ui/Button';
import styled from 'styled-components';

export interface UnitType {
  id: string;
  type: string;
  count: string;
  houseNumbers: string[];
  rent: string;
  deposit: string;
  size?: string;
  bedrooms?: string;
  bathrooms?: string;
}

interface UnitTypesSectionProps {
  unitTypes: UnitType[];
  totalUnits: number;
  availableUnitNumbers: string[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Omit<UnitType, 'id'>>) => void;
  error?: string;
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--card) 92%, var(--primary) 8%), var(--card));
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 18px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitleLabel = styled(Label)`
  font-size: 16px;
  font-weight: 600;
  color: var(--foreground);
`;

const SectionDescription = styled.div`
  font-size: 0.875rem;
  color: var(--muted-foreground);
`;

const SummaryStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--background);
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: var(--muted);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number; $over: boolean }>`
  height: 100%;
  width: ${p => p.$percent}%;
  background: ${p => p.$over ? 'var(--destructive)' : 'var(--primary)'};
  transition: width 0.25s ease;
`;

const GuideBox = styled.div`
  display: flex;
  gap: 12px;
  background: var(--primary-50, rgba(16,185,129,0.05));
  border: 1px solid var(--primary-100, rgba(16,185,129,0.2));
  border-radius: 8px;
  padding: 16px;
  color: var(--foreground);
  font-size: 13px;
  line-height: 1.6;

  .icon {
    color: var(--primary);
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--primary-700, #047857);
  }

  ol {
    margin: 0;
    padding-left: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const FieldLabel = styled(Label)`
  font-size: 13px;
  font-weight: 500;
  color: var(--foreground);
  margin-bottom: 6px;
`;

const UnitTypesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const UnitTypeCard = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-color: color-mix(in srgb, var(--primary) 35%, var(--border));
  }
`;

const UnitTypeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px 10px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--muted) 60%, transparent);
`;

const UnitTypeHeaderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--foreground);
`;

const UnitTypeMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
`;

const MetaBadge = styled.span<{ $tone?: 'neutral' | 'warning' | 'success' }>`
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: ${p =>
    p.$tone === 'warning'
      ? 'rgba(245, 158, 11, 0.14)'
      : p.$tone === 'success'
        ? 'rgba(16, 185, 129, 0.14)'
        : 'var(--background)'};
  color: ${p =>
    p.$tone === 'warning'
      ? '#b45309'
      : p.$tone === 'success'
        ? '#047857'
        : 'var(--muted-foreground)'};
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr);
  gap: 16px;
  margin-bottom: 12px;
  padding: 0 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    padding-right: 0;
  }
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSelect = styled.select`
  height: 44px;
  width: 100%;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--background));
  border-radius: 8px;
  padding: 0 14px;
  font-size: 15px;
  color: hsl(var(--foreground));
  outline: none;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.5 4.5L6 8L9.5 4.5' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  
  /* Fallback color for the SVG to respect dark mode text */
  & {
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.5 4.5L6 8L9.5 4.5' stroke='%23888888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  }

  &:hover {
    border-color: hsl(var(--primary) / 0.5);
    background: hsl(var(--muted) / 0.3);
  }

  &:focus {
    border-color: hsl(var(--primary));
    box-shadow: 0 0 0 3px hsl(var(--primary) / 0.15);
    background: hsl(var(--background));
  }

  /* Make sure dropdown options respond properly to the host OS theme */
  option {
    background-color: hsl(var(--background));
    color: hsl(var(--foreground));
    padding: 10px;
    font-size: 15px;
  }
`;

const HouseSelectionSection = styled.div`
  background: color-mix(in srgb, var(--muted) 75%, transparent);
  border-radius: 8px;
  padding: 16px;
  margin: 0 10px 12px;
  border: 1px solid color-mix(in srgb, var(--border) 80%, var(--muted));
`;

/** Scrollable area so large unit counts don’t stretch the page; grid stays multi-select. */
const AssignUnitsScroll = styled.div`
  margin-top: 12px;
  max-height: min(320px, 55vh);
  overflow-y: auto;
  padding: 4px 2px 8px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--primary) 20%, var(--border));
  background: color-mix(in srgb, var(--background) 90%, var(--muted));
`;

const HouseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 8px;
`;

const HousePill = styled.button<{ $selected: boolean; $disabled: boolean }>`
  /* Selected: green fill + border; unselected: neutral — easy to scan at a glance */
  background: ${p =>
    p.$selected
      ? 'color-mix(in srgb, rgb(16 185 129) 38%, var(--background))'
      : 'var(--muted)'};
  color: ${p =>
    p.$selected ? 'rgb(6 95 70)' : 'var(--foreground)'};
  border: 2px solid ${p => (p.$selected ? 'rgb(16 185 129)' : 'transparent')};
  opacity: ${p => p.$disabled ? 0.5 : 1};
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  border-radius: 10px;
  padding: 10px 12px;
  min-height: 42px;
  font-size: 14px;
  font-weight: ${p => (p.$selected ? 700 : 500)};
  transition: all 0.2s ease;

  ${p =>
    p.$selected &&
    `
    @media (prefers-color-scheme: dark) {
      color: rgb(167 243 208);
      background: color-mix(in srgb, rgb(16 185 129) 32%, var(--background));
    }
  `}

  &:hover:not(:disabled) {
    border-color: ${p =>
      p.$selected
        ? 'rgb(5 150 105)'
        : 'color-mix(in srgb, rgb(16 185 129) 35%, transparent)'};
    background: ${p =>
      p.$selected
        ? 'color-mix(in srgb, rgb(16 185 129) 48%, var(--background))'
        : 'color-mix(in srgb, rgb(16 185 129) 12%, var(--muted))'};
    color: ${p => (p.$selected ? undefined : 'rgb(5 150 105)')};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgb(16 185 129);
    outline-offset: 2px;
  }
`;

const HousePillContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
`;

const ToolbarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const ToolbarTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--foreground);
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: var(--background);
  border: 1px solid var(--border);
  color: var(--foreground);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--muted);
    border-color: var(--primary);
    color: var(--primary);
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  background: var(--background);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--border);
  margin: 0 10px 10px;
`;

const InlineHint = styled.div`
  font-size: 12px;
  color: var(--muted-foreground);
  margin-top: 6px;
`;

const AddButton = styled(Button)`
  gap: 6px;
  font-weight: 500;
`;

const RemoveButton = styled.button`
  background: var(--muted);
  border: 1px solid var(--border);
  color: var(--muted-foreground);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 22px;
  right: 24px;
  transition: all 0.2s;

  &:hover {
    background: var(--destructive);
    color: white;
    border-color: var(--destructive);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.2);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
`;

const unitTypeOptions = [
  { value: 'BEDSITTER', label: 'Bedsitter' },
  { value: 'STUDIO', label: 'Studio' },
  { value: 'ONE_BEDROOM', label: '1 Bedroom' },
  { value: 'TWO_BEDROOM', label: '2 Bedroom' },
  { value: 'THREE_BEDROOM', label: '3 Bedroom' },
  { value: 'FOUR_BEDROOM', label: '4 Bedroom' },
  { value: 'FIVE_BEDROOM', label: '5 Bedroom' },
  { value: 'COMMERCIAL', label: 'Commercial' }
];

export const UnitTypesSection: React.FC<UnitTypesSectionProps> = ({
  unitTypes,
  totalUnits,
  availableUnitNumbers,
  onAdd,
  onRemove,
  onUpdate,
  error
}) => {
  const totalAssignedUnits = unitTypes.reduce((sum, ut) => sum + ut.houseNumbers.length, 0);
  const remainingUnits = totalUnits - totalAssignedUnits;
  const assignedPercent = totalUnits > 0 ? Math.min(100, Math.round((totalAssignedUnits / totalUnits) * 100)) : 0;

  const housesAssignedToOthers = (currentId: string) => {
    const set = new Set<string>();
    unitTypes.forEach(ut => {
      if (ut.id === currentId) return;
      ut.houseNumbers.forEach(h => set.add(h));
    });
    return set;
  };

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>
          <SectionTitleLabel>Unit Types & Setup</SectionTitleLabel>
          <SectionDescription>
            Categorize your property units and set their monthly rent
            {totalUnits > 0 && unitTypes.length > 0 && (
              <span style={{ marginLeft: '6px', fontWeight: 500, color: remainingUnits < 0 ? '#dc2626' : 'var(--primary)' }}>
                ({totalAssignedUnits}/{totalUnits} assigned)
              </span>
            )}
          </SectionDescription>
        </SectionTitle>
        <AddButton type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus size={16} />
          Add Category
        </AddButton>
      </SectionHeader>

      <GuideBox>
        <Info className="icon" size={20} />
        <div className="content">
          <h4>How to group your units</h4>
          <ol>
            <li>Select a Unit Type from the dropdown (e.g., Studio, 1 Bedroom).</li>
            <li>Click on the specific house numbers below to assign them to this category.</li>
            <li>Set the default rent and deposit. You can always override these per tenant later!</li>
          </ol>
        </div>
      </GuideBox>

      <SummaryStrip>
        <SummaryRow>
          <span>
            <strong>{totalAssignedUnits}</strong> of <strong>{totalUnits}</strong> units assigned
          </span>
          <span style={{ color: remainingUnits < 0 ? 'var(--destructive)' : 'var(--muted-foreground)' }}>
            {remainingUnits >= 0 ? `${remainingUnits} remaining` : `${Math.abs(remainingUnits)} over assigned`}
          </span>
        </SummaryRow>
        <ProgressTrack>
          <ProgressFill $percent={assignedPercent} $over={remainingUnits < 0} />
        </ProgressTrack>
      </SummaryStrip>

      {unitTypes.length > 0 && (
        <UnitTypesList>
          {unitTypes.map((unitType) => {
            const assignedToOthers = housesAssignedToOthers(unitType.id);
            const selectedCount = unitType.houseNumbers.length;
            const selectableCount = availableUnitNumbers.filter(h => !assignedToOthers.has(h) || unitType.houseNumbers.includes(h)).length;

            return (
              <UnitTypeCard key={unitType.id}>
                <UnitTypeHeader>
                  <UnitTypeHeaderTitle>
                    {unitType.type ? unitTypeOptions.find(opt => opt.value === unitType.type)?.label || 'Unit Category' : 'New Unit Category'}
                  </UnitTypeHeaderTitle>
                  <UnitTypeMeta>
                    <MetaBadge $tone={selectedCount > 0 ? 'success' : 'neutral'}>{selectedCount} selected</MetaBadge>
                    <MetaBadge $tone={selectableCount === 0 ? 'warning' : 'neutral'}>{selectableCount} available</MetaBadge>
                  </UnitTypeMeta>
                </UnitTypeHeader>

                {unitTypes.length > 1 && (
                  <RemoveButton type="button" onClick={() => onRemove(unitType.id)} title="Remove this category">
                    <X size={16} />
                  </RemoveButton>
                )}

                <TopRow>
                  <FieldContainer>
                    <FieldLabel htmlFor={`type-${unitType.id}`}>Select Unit Type</FieldLabel>
                    <StyledSelect
                      id={`type-${unitType.id}`}
                      value={unitType.type}
                      onChange={(e) => onUpdate(unitType.id, { type: e.target.value })}
                    >
                      <option value="">-- Choose type --</option>
                      {unitTypeOptions.map((option) => (
                        <option key={option.value} value={option.value} style={{background:'transparent'}}>
                          {option.label}
                        </option>
                      ))}
                    </StyledSelect>
                  </FieldContainer>
                </TopRow>

                <HouseSelectionSection>
                  <ToolbarHeader>
                    <ToolbarTitle>
                      Assign units
                      <span style={{ color: 'var(--muted-foreground)', fontWeight: 'normal', marginLeft: '6px' }}>
                        ({unitType.houseNumbers.length} selected)
                      </span>
                    </ToolbarTitle>
                    <ToolbarActions>
                      <ActionButton
                        type="button"
                        onClick={() => {
                          if (!unitType.type) return;
                          const selectable = availableUnitNumbers.filter(h => !assignedToOthers.has(h));
                          onUpdate(unitType.id, {
                            houseNumbers: selectable,
                            count: String(selectable.length)
                          });
                        }}
                      >
                        Select All Available
                      </ActionButton>
                      <ActionButton
                        type="button"
                        onClick={() => {
                          onUpdate(unitType.id, { houseNumbers: [], count: '0' });
                        }}
                      >
                        Clear Selection
                      </ActionButton>
                    </ToolbarActions>
                  </ToolbarHeader>

                  {!unitType.type ? (
                    <InlineHint style={{ marginTop: '12px', fontSize: '13px' }}>Please select a Unit Type above to start assigning houses.</InlineHint>
                  ) : (
                    <>
                      <InlineHint style={{ marginTop: '4px' }}>
                        Click unit numbers to toggle — multiple selection. Works with any naming convention.
                      </InlineHint>
                      <AssignUnitsScroll>
                        <HouseGrid>
                          {availableUnitNumbers.map((houseNo) => {
                            const selected = unitType.houseNumbers.includes(houseNo);
                            const disabled = !selected && assignedToOthers.has(houseNo);

                            return (
                              <HousePill
                                key={houseNo}
                                type="button"
                                $selected={selected}
                                $disabled={disabled}
                                aria-pressed={selected}
                                title={disabled ? 'Assigned to another category' : ''}
                                onClick={() => {
                                  if (disabled) return;
                                  const next = selected
                                    ? unitType.houseNumbers.filter(h => h !== houseNo)
                                    : [...unitType.houseNumbers, houseNo];
                                  onUpdate(unitType.id, {
                                    houseNumbers: next,
                                    count: String(next.length)
                                  });
                                }}
                              >
                                <HousePillContent>
                                  {houseNo}
                                </HousePillContent>
                              </HousePill>
                            );
                          })}
                        </HouseGrid>
                      </AssignUnitsScroll>
                    </>
                  )}

                </HouseSelectionSection>

                <DetailsGrid>
                  <FieldContainer>
                    <FieldLabel htmlFor={`rent-${unitType.id}`}>Rent/Month (KES)</FieldLabel>
                    <Input
                      id={`rent-${unitType.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitType.rent}
                      onChange={(e) => onUpdate(unitType.id, { rent: e.target.value })}
                      placeholder="e.g. 15000"
                      style={{ height: '40px' }}
                    />
                  </FieldContainer>

                  <FieldContainer>
                    <FieldLabel htmlFor={`deposit-${unitType.id}`}>Deposit (KES)</FieldLabel>
                    <Input
                      id={`deposit-${unitType.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitType.deposit}
                      onChange={(e) => onUpdate(unitType.id, { deposit: e.target.value })}
                      placeholder="e.g. 15000"
                      style={{ height: '40px' }}
                    />
                  </FieldContainer>

                  <FieldContainer>
                    <FieldLabel htmlFor={`bedrooms-${unitType.id}`}>Bedrooms</FieldLabel>
                    <Input
                      id={`bedrooms-${unitType.id}`}
                      type="number"
                      min="0"
                      value={unitType.bedrooms || ''}
                      onChange={(e) => onUpdate(unitType.id, { bedrooms: e.target.value })}
                      placeholder="Optional"
                      style={{ height: '40px' }}
                    />
                  </FieldContainer>
                  
                  <FieldContainer>
                    <FieldLabel htmlFor={`bathrooms-${unitType.id}`}>Bathrooms</FieldLabel>
                    <Input
                      id={`bathrooms-${unitType.id}`}
                      type="number"
                      min="0"
                      value={unitType.bathrooms || ''}
                      onChange={(e) => onUpdate(unitType.id, { bathrooms: e.target.value })}
                      placeholder="Optional"
                      style={{ height: '40px' }}
                    />
                  </FieldContainer>
                </DetailsGrid>

              </UnitTypeCard>
            );
          })}
        </UnitTypesList>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </SectionContainer>
  );
};
